import { LocalClusteredMarkers } from "@/components/map/map-count-cluster";
import {
  INSPECTION_MAP_DEFAULT_CENTER,
  INSPECTION_MAP_DEFAULT_ZOOM,
  INSPECTION_MAP_MAX_ZOOM,
  useInspectionMapFitBounds,
} from "@/hooks/use-inspection-map-bounds";
import type { LatLng } from "@/lib/geo";
import type { PhotoAnswer } from "@/lib/photo-answer";
import {
  formatPhotoTimeRange,
  shouldShowSeparatePropertyPin,
} from "@/lib/photo-map-pins";
import type { MapCenter } from "@/types/map";
import {
  AdvancedMarker,
  Map as GoogleMap,
  InfoWindow,
  Pin,
} from "@vis.gl/react-google-maps";
import { useMemo, useState } from "react";

type PhotoAnswerLocationMapProps = {
  photo: PhotoAnswer;
  propertyLocation?: LatLng | null;
  className?: string;
};

/**
 * Small map next to a reviewed photo answer: photo pin always, plus a separate
 * property pin when the capture location is farther than the near-property
 * threshold from the property.
 */
export function PhotoAnswerLocationMap({
  photo,
  propertyLocation = null,
  className,
}: PhotoAnswerLocationMapProps) {
  const showProperty = shouldShowSeparatePropertyPin(photo, propertyLocation);

  const pins = useMemo(() => {
    const list: Array<{
      id: string;
      latitude: number;
      longitude: number;
      lat: number;
      lng: number;
      label: string;
      kind: "photo" | "property";
    }> = [
      {
        id: "photo",
        latitude: photo.latitude,
        longitude: photo.longitude,
        lat: photo.latitude,
        lng: photo.longitude,
        label: formatPhotoTimeRange([photo]) ?? "Photo location",
        kind: "photo",
      },
    ];
    if (
      showProperty &&
      propertyLocation &&
      Number.isFinite(propertyLocation.latitude) &&
      Number.isFinite(propertyLocation.longitude)
    ) {
      list.push({
        id: "property",
        latitude: propertyLocation.latitude,
        longitude: propertyLocation.longitude,
        lat: propertyLocation.latitude,
        lng: propertyLocation.longitude,
        label: "Property location",
        kind: "property",
      });
    }
    return list;
  }, [photo, propertyLocation, showProperty]);

  const mapCenter = useMemo<MapCenter>(() => {
    if (pins.length === 0) return INSPECTION_MAP_DEFAULT_CENTER;
    const lats = pins.map((p) => p.latitude);
    const lngs = pins.map((p) => p.longitude);
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    };
  }, [pins]);

  return (
    <div
      className={
        className ??
        "h-28 w-full min-w-[8rem] max-w-[14rem] overflow-hidden rounded border border-border sm:h-32"
      }
      data-testid="photo-answer-location-map"
      data-show-property-pin={showProperty ? "true" : "false"}
    >
      <GoogleMap
        mapId="photo-answer-location-map"
        defaultCenter={mapCenter}
        defaultZoom={INSPECTION_MAP_DEFAULT_ZOOM}
        maxZoom={INSPECTION_MAP_MAX_ZOOM}
        gestureHandling="cooperative"
        disableDefaultUI
        className="h-full w-full"
      >
        <PhotoAnswerLocationMapBounds pins={pins} />
        <LocalClusteredMarkers
          points={pins}
          renderPoint={(pin) => <AnswerLocationMarker key={pin.id} pin={pin} />}
        />
      </GoogleMap>
    </div>
  );
}

function PhotoAnswerLocationMapBounds({
  pins,
}: {
  pins: Array<{ latitude: number; longitude: number }>;
}) {
  useInspectionMapFitBounds(pins);
  return null;
}

function AnswerLocationMarker({
  pin,
}: {
  pin: {
    id: string;
    latitude: number;
    longitude: number;
    lat: number;
    lng: number;
    label: string;
    kind: "photo" | "property";
  };
}) {
  const [showInfo, setShowInfo] = useState(false);
  const isPhoto = pin.kind === "photo";

  return (
    <>
      <AdvancedMarker
        position={{ lat: pin.latitude, lng: pin.longitude }}
        title={pin.label}
        onMouseEnter={() => setShowInfo(true)}
        onMouseLeave={() => setShowInfo(false)}
        onClick={() => setShowInfo((open) => !open)}
      >
        <Pin
          background={isPhoto ? "#2563eb" : "#111111"}
          borderColor={isPhoto ? "#1d4ed8" : "#000000"}
          glyphColor="#ffffff"
        />
      </AdvancedMarker>
      {showInfo ? (
        <InfoWindow
          position={{ lat: pin.latitude, lng: pin.longitude }}
          disableAutoPan
          headerDisabled
          onCloseClick={() => setShowInfo(false)}
        >
          <div
            className="max-w-[12rem] text-sm"
            data-testid={`answer-map-popup-${pin.id}`}
          >
            <p className="font-medium">{isPhoto ? "Photo" : "Property"}</p>
            <p>{pin.label}</p>
          </div>
        </InfoWindow>
      ) : null}
    </>
  );
}
